import HumidityMax from "../Assets/RealTimeData/HumidityMax.svg";
import TemperatureMax from "../Assets/RealTimeData/TemperatureMax.svg";
import TemperatureMin from "../Assets/RealTimeData/TemperatureMin.svg";
import HumidityAvg from "../Assets/RealTimeData/HumidityAvg.svg";
import HumidityMin from "../Assets/RealTimeData/HumidityMin.svg";
import WindSpeedAvg from "../Assets/RealTimeData/WindSpeedAvg.svg";
import Rainfall from "../Assets/RealTimeData/Rainfall.svg";
import LocationSelector from "../Components/Common/LocationSelector.jsx";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FormattedWeather from "../api/FormattedWeather.js";
import {
  setFormattedWeather,
  setPlaceName,
} from "../ReduxStore/App/DashBoard/dashboardSlice";
import getPlaceName from "../api/PlaceName.js";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

function WeatherRow({ icon, label, value, color }) {
  const colorClasses = {
    blue: "bg-blue-100 dark:bg-blue-900",
    red: "bg-red-100 dark:bg-red-900",
    sky: "bg-sky-100 dark:bg-sky-900",
    purple: "bg-purple-100 dark:bg-purple-900",
    indigo: "bg-indigo-100 dark:bg-indigo-900",
    violet: "bg-violet-100 dark:bg-violet-900",
    teal: "bg-teal-100 dark:bg-teal-900",
  };

  return (
    <div className="flex items-center justify-between group/row">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 flex items-center justify-center ${colorClasses[color] || "bg-gray-100"} bg-opacity-50 dark:bg-opacity-30 rounded-xl transition-all duration-300`}>
          <img className="w-5 h-5 group-hover/row:scale-110 transition-transform duration-300" src={icon} alt={label} />
        </div>
        <span className="text-sm font-semibold opacity-70 tracking-wide dark:text-darkModeBlackPrimaryText">{label}</span>
      </div>
      <span className="text-sm font-extrabold tracking-tight dark:text-darkModeBlackPrimaryText">{value}</span>
    </div>
  );
}

function RealTimeData() {
  const dispatch = useDispatch()
  const { formattedWeather, placeName, latitude, longitude } = useSelector(
    (store) => store.dashboard
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const addError = (errorMessage) => {
    setErrors((prev) => [...prev, errorMessage])
  }
  // ===============format date ============================
  function formatDate(dateString) {
    const dateObj = new Date(dateString);

    // Define options for the formatting
    const options = { year: "numeric", month: "short", day: "numeric" };

    // Convert and format the date
    return dateObj.toLocaleDateString("en-US", options);
  }
  // ===============format date ============================
  async function getFormattedWeatherData(lat, lng) {
    let errorMessage = null;
    try {
      let res = await FormattedWeather(lat, lng);
      if (res && res['Forecast data']) {
        dispatch(setFormattedWeather(res[`Forecast data`]));
      } else {
        errorMessage = 'Invalid response format from FormattedWeather API'
        console.error(errorMessage);
        dispatch(setFormattedWeather([]));
        throw new Error("")
      }
    } catch (error) {
      console.log("error:", error);
      dispatch(setFormattedWeather([]));
      throw new Error(errorMessage ? errorMessage : "Could not fetch Formatted Weather Data")
    }
  }
  async function getPlaceWithLatLng(lat, lng) {
    let errorMessage = null;
    try {
      let res = await getPlaceName(lat, lng);
      if (res && res.location) {
        dispatch(setPlaceName(res.location.village, res.location.sub_dist));
      } else {
        errorMessage = 'Invalid response format from Place name lookup'
        throw new Error("")
      }
    } catch (error) {
      console.log("error:", error);
      throw new Error(errorMessage ? errorMessage : "Place name lookup failed");
    }
  }

  async function loadData() {
    // Only fetch data if we don't have formattedWeather data or if coordinates changed
    if (latitude && longitude && (!formattedWeather || formattedWeather.length === 0)) {
      console.log('RealTimeData: Fetching data for coordinates:', latitude, longitude);
      setLoading(true);
      setErrors([]);
      const results = await Promise.allSettled([
        getFormattedWeatherData(latitude, longitude),
        getPlaceWithLatLng(latitude, longitude)
      ]);

      results.forEach((result) => {
        if (result.status === "rejected") {
          addError(result.reason.message || "Unknown error occurred");
        }
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData()
  }, [latitude, longitude]);

  const todayStr = new Date().toISOString().split("T")[0];
  const todayIndex = formattedWeather?.findIndex(item => item.Date_time?.startsWith(todayStr)) || 0;

  if (loading) {
    return (
      <div className="h-full w-full bg-primaryBgColor dark:bg-darkModePrimaryBgColor rounded-md overflow-hidden relative ">
        <Skeleton
          height="100%"
          baseColor="transparent"
          highlightColor="rgba(255, 255, 255, 0.2)"
        />
      </div>
    );
  }

  if (errors.length > 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-primaryBgColor dark:bg-darkModePrimaryBgColor">
        <div className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 px-6 py-5 rounded-md shadow-md max-w-md">
          <h2 className="text-lg font-semibold mb-3"> Failed to load data</h2>
          <ul className="list-disc list-inside text-sm mb-4 space-y-1">
            {errors.map((err, idx) => (<li key={idx}>{err}</li>))}
          </ul>
          <button onClick={loadData} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition" > Try Again </button>
        </div>
      </div>
    );
  }

  return (
    <div className=" h-full">
      <div className="py-4 h-full flex flex-col font-semibold gap-4  bg-primaryBgColor dark:bg-darkModePrimaryBgColor text-blackPrimaryText dark:text-darkModeBlackPrimaryText px-2 rounded-lg ">
        <div className="flex flex-col sm:flex-row justify-between items-center px-6 py-4 mb-4 bg-cardBgColor dark:bg-darkModeSecondaryBgColor rounded-lg border border-gray-200 dark:border-white/10 shadow-sm mx-4">
          <div className="text-center sm:text-left mb-4 sm:mb-0">
            <h1 className="text-2xl font-extrabold text-wsText dark:text-darkModeBlackPrimaryText tracking-tight">Weather Insights</h1>
            <p className="text-sm opacity-70 dark:text-darkModeBlackPrimaryText dark:opacity-60">Near-realtime forecast for the next 16+ days</p>
          </div>
          <LocationSelector />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-6 px-4 pb-12 overflow-y-auto flex-grow custom-scrollbar">
          {formattedWeather?.map((realTimeData, index) => (
            <div
              key={index}
              className="flex flex-col bg-cardBgColor dark:bg-darkModeCardBgColor border border-gray-200 dark:border-white/10 rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 p-6 group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-blue-400 bg-opacity-10 rounded-md blur-2xl group-hover:bg-opacity-20 transition-all"></div>

              <div className="text-center mb-6 pb-4 border-b border-white border-opacity-20 dark:border-white dark:border-opacity-10">
                <p className="text-wsText dark:text-darkModeBlackPrimaryText font-extrabold text-xl group-hover:scale-105 transition-transform duration-500">
                  {index === todayIndex
                    ? "Today"
                    : formatDate(realTimeData.Date_time)}
                </p>
              </div>
              <div className="flex flex-col gap-4 relative z-10">
                <WeatherRow icon={Rainfall} label="Rainfall" value={`${Math.trunc(realTimeData.Rainfall)} mm`} color="blue" />
                <WeatherRow icon={TemperatureMax} label="T Max" value={`${Math.trunc(realTimeData.Tmax)} °C`} color="red" />
                <WeatherRow icon={TemperatureMin} label="T Min" value={`${Math.trunc(realTimeData.Tmin)} °C`} color="sky" />
                <WeatherRow icon={HumidityMax} label="H Max" value={`${Math.trunc(realTimeData.RH_max)} %`} color="purple" />
                <WeatherRow icon={HumidityMin} label="H Min" value={`${Math.trunc(realTimeData.RH_min)} %`} color="indigo" />
                <WeatherRow icon={HumidityAvg} label="H Avg" value={`${Math.trunc(realTimeData.RH)} %`} color="violet" />

                <div className="border-t border-white border-opacity-10 dark:border-white dark:border-opacity-10 pt-4 mt-2">
                  <WeatherRow icon={WindSpeedAvg} label="Wind Speed" value={`${Math.trunc(realTimeData.Wind_Speed)} m/s`} color="teal" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default RealTimeData;
